import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Table, Breadcrumb, Form, Button, InputGroup } from 'react-bootstrap';
import { Formik, Field, FieldArray } from 'formik';
import dayjs from 'dayjs';
import * as yup from 'yup';

import IconTrueOrFalse from 'Components/Shared/IconTrueOrFalse';
import CustomModal from 'Components/UI/CustomModal';

import { AiOutlinePlusCircle, AiOutlineArrowUp, AiOutlineArrowDown } from 'react-icons/ai';
import { FiEdit, FiTrash2, FiPlus, FiMinus } from 'react-icons/fi';

import { getGeneratedKey, getAllApiKeys, saveApiKey, deleteApiKey } from 'Services/ApiKeyService';
import AppService from 'Services/AppService';

const schema = yup.object({
    description: yup.string(),
    appName: yup.string().required(),
    appId: yup.string().nullable(),
    source: yup.string().required(),
    targetAppId: yup.string().required(),
    expiration: yup.date().nullable(),
    sharedKey: yup.string().required(),
    allRoutesPass: yup.boolean().required(),
    routeNamesAllowed: yup.array().of(yup.string().required()).nullable(),
});

const Apps = () => {
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showFormModal, setShowFormModal] = useState(false);
    const [formModalEditMode, setFormModalEditMode] = useState(false);
    const [showDateSwitchs, setShowDateSwitchs] = useState(true);
    const [expire, setExpire] = useState(null);
    const [apps, setApps] = useState([]);
    const [apiKey, setApiKey] = useState(null);
    const [apiKeys, setApiKeys] = useState([]);

    useEffect(() => {
        getAllApiKeys()
            .then((result) => {
                if (result.status === 200) {
                    setApiKeys(result.data);
                }
            })
            .catch();

        AppService.getApps()
            .then((result) => {
                if (result.status === 200) {
                    setApps(result.data);
                }
            })
            .catch();
    }, []);

    const handleDeleteModalClose = () => {
        setApiKey(null);
        setShowDeleteModal(false);
    };

    const handleDeleteModalOpen = (data) => {
        setApiKey(data);
        setShowDeleteModal(true);
    };

    const handleFormModalClose = () => {
        setExpire(null);
        setApiKey(null);
        setShowFormModal(false);
    };

    const handleFormModalOpen = (editMode = false, apiKeyData = null) => {
        if (apiKeyData) setApiKey(apiKeyData);
        setFormModalEditMode(editMode);
        setShowDateSwitchs(!editMode);
        setShowFormModal(true);
    };

    const generateSharedKey = async () => {
        try {
            const result = await getGeneratedKey();
            if (result.status === 200) {
                return result.data;
            }
            return null;
        } catch (Exception) {
            return null;
        }
    };

    const handleApiKeyDelete = (data) => {
        deleteApiKey(data.apiKeyId)
            .then((result) => {
                if (result.status === 200) {
                    toast.success('API-KEY a été supprimé avec succès');
                    const apiKeysDATA = [...apiKeys].filter((d) => d.apiKeyId !== data.apiKeyId);
                    setApiKeys(apiKeysDATA);
                    handleDeleteModalClose();
                }
            })
            .catch();
    };

    const submitForm = (values) => {
        const apiKeyValues = {
            ...values,
            appId: values.appId && values.appId !== '' ? values.appId : null,
            routeNamesAllowed:
                values.routeNamesAllowed && values.routeNamesAllowed.length > 0
                    ? JSON.stringify(values.routeNamesAllowed)
                    : null,
        };
        saveApiKey(apiKeyValues)
            .then((result) => {
                if (result.status === 200) {
                    const resultData = result.data;
                    toast.success('API-KEY a été enregistré avec succès');
                    const apiKeysDATA = [...apiKeys].filter((dd) => dd.apiKeyId !== resultData.apiKeyId);
                    apiKeysDATA.unshift(resultData);
                    setApiKeys(apiKeysDATA);
                    handleFormModalClose();
                }
            })
            .catch();
    };

    return (
        <>
            <Breadcrumb className="mt-3">
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/' }}>
                    Home
                </Breadcrumb.Item>
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/admin' }}>
                    Admin
                </Breadcrumb.Item>
                <Breadcrumb.Item active>Api-Keys</Breadcrumb.Item>
            </Breadcrumb>
            <div className="d-flex justify-content-between align-items-center py-2">
                <h2>Api-Keys</h2>
                <button onClick={() => handleFormModalOpen()} className="btn btn-outline-primary btn-sm">
                    <AiOutlinePlusCircle />
                    <span className="ml-1">New</span>
                </button>
            </div>

            <Table striped bordered hover responsive>
                <thead>
                    <tr>
                        <th className="text-center">Source</th>
                        <th className="text-center">Shared Key</th>
                        <th>App</th>
                        <th>Targeted App</th>
                        <th className="text-center">Expiration</th>
                        <th className="text-center">Pass</th>
                        <th className="text-center">Edit</th>
                        <th className="text-center">Delete</th>
                    </tr>
                </thead>
                <tbody>
                    {apiKeys.length ? (
                        apiKeys.map((apiKeyData) => {
                            return (
                                <tr key={apiKeyData.apiKeyId}>
                                    <td className="text-center">{apiKeyData.source}</td>
                                    <td className="text-center">
                                        <i
                                            className="my_spoiler-text"
                                            style={{
                                                fontSize: '9pt',
                                                fontStyle: 'normal',
                                            }}
                                        >
                                            {apiKeyData.sharedKey}
                                        </i>
                                    </td>
                                    <td>{apiKeyData.appName}</td>
                                    <td>{apiKeyData.targetApp.name}</td>
                                    <td className="text-center">
                                        {apiKeyData.expiration
                                            ? dayjs(apiKeyData.expiration).format('DD/MM/YYYY')
                                            : 'Never'}
                                    </td>
                                    <td className="text-center">
                                        <IconTrueOrFalse value={apiKeyData.allRoutesPass} falseColor="#d36f2f" />
                                    </td>

                                    <td className="text-center">
                                        <FiEdit
                                            onClick={() => handleFormModalOpen(true, apiKeyData)}
                                            style={{
                                                fontSize: '16pt',
                                                color: '#516be5',
                                                cursor: 'pointer',
                                            }}
                                        />
                                    </td>

                                    <td className="text-center">
                                        <FiTrash2
                                            onClick={() => handleDeleteModalOpen(apiKeyData)}
                                            style={{
                                                fontSize: '16pt',
                                                color: '#cf4e4e',
                                                cursor: 'pointer',
                                            }}
                                        />
                                    </td>
                                </tr>
                            );
                        })
                    ) : (
                        <tr>
                            <td colSpan="8">no data</td>
                        </tr>
                    )}
                </tbody>
            </Table>
            <CustomModal
                title="Delete Api-Key"
                size="sm"
                show={showDeleteModal}
                handleClose={handleDeleteModalClose}
                footerCenter={true}
            >
                <div className="row">
                    <div className="col-md-12 d-flex flex-column align-items-center">
                        <div>
                            Key: &nbsp;
                            <i style={{ fontSize: '8pt', fontStyle: 'unset' }}>{apiKey && apiKey.sharedKey}</i>
                        </div>
                        <img src="/images/alert.svg" alt="" style={{ width: '100pt' }} className="d-block" />
                        <h4 className="py-3">Irreversible Action</h4>
                        <button className="btn btn-danger  btn-block" onClick={() => handleApiKeyDelete(apiKey)}>
                            Delete {apiKey && `${apiKey.appName} => ${apiKey.targetApp.name}`}
                        </button>
                    </div>
                </div>
            </CustomModal>
            <CustomModal
                title={`${!!formModalEditMode ? 'Edit' : 'New'} Api-Key`}
                size="lg"
                show={showFormModal}
                handleClose={handleFormModalClose}
            >
                <div>
                    <Formik
                        validationSchema={schema}
                        onSubmit={(values) => submitForm(values)}
                        enableReinitialize={true}
                        initialValues={{
                            apiKeyId: apiKey ? apiKey.apiKeyId : null,
                            description: apiKey ? apiKey.description : '',
                            appName: apiKey ? apiKey.appName : '',
                            appId: apiKey && apiKey.appId ? apiKey.appId : null,
                            source: apiKey ? apiKey.source : 'External',
                            targetAppId: apiKey && apiKey.targetAppId ? apiKey.targetAppId : undefined,
                            expiration: apiKey ? apiKey.expiration : null,
                            sharedKey: apiKey ? apiKey.sharedKey : '',
                            allRoutesPass: apiKey ? apiKey.allRoutesPass : false,
                            routeNamesAllowed: apiKey ? JSON.parse(apiKey.routeNamesAllowed) : null,
                        }}
                    >
                        {({ handleSubmit, handleChange, values, errors }) => (
                            <Form noValidate onSubmit={handleSubmit}>
                                <Form.Group controlId="exampleForm.ControlTextarea1">
                                    <Form.Label>Description</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={2}
                                        name="description"
                                        value={values.description}
                                        onChange={handleChange}
                                        isInvalid={!!errors.description}
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.description}</Form.Control.Feedback>
                                </Form.Group>
                                <Form.Group controlId="formBasicEmail">
                                    <Form.Label>App Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="App Name"
                                        name="appName"
                                        value={values.appName}
                                        onChange={handleChange}
                                        isInvalid={!!errors.appName}
                                        readOnly={Boolean(values.appId)}
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.appName}</Form.Control.Feedback>
                                </Form.Group>
                                <Form.Group controlId="exampleForm.ControlTextarea99">
                                    <Form.Label>App</Form.Label>
                                    <Form.Control
                                        as="select"
                                        name="appId"
                                        value={values.appId}
                                        isInvalid={!!errors.appId}
                                        onChange={({ target: { value: inputValue } }) => {
                                            handleChange({
                                                target: {
                                                    value: inputValue,
                                                    name: 'appId',
                                                },
                                            });
                                            let app_name = '';
                                            if (inputValue) {
                                                const appEntry = apps.find((dd) => dd.appId === inputValue);
                                                if (appEntry) app_name = appEntry.name;
                                            } else {
                                                handleChange({
                                                    target: {
                                                        value: 'External',
                                                        name: 'source',
                                                    },
                                                });
                                            }
                                            handleChange({
                                                target: {
                                                    value: app_name,
                                                    name: 'appName',
                                                },
                                            });
                                        }}
                                    >
                                        <option value="">External App</option>
                                        {apps.map((appdata) => (
                                            <option key={appdata.appId} value={appdata.appId}>
                                                {appdata.name}
                                            </option>
                                        ))}
                                    </Form.Control>
                                    <Form.Control.Feedback type="invalid">{errors.appId}</Form.Control.Feedback>
                                </Form.Group>
                                <Form.Group controlId="exampleForm.ControlTextarea99">
                                    <Form.Label>Source</Form.Label>
                                    <Form.Control
                                        as="select"
                                        name="source"
                                        value={values.source}
                                        onChange={handleChange}
                                        isInvalid={!!errors.source}
                                    >
                                        <option value="Internal">Internal</option>
                                        <option value="External">External</option>
                                    </Form.Control>
                                    <Form.Control.Feedback type="invalid">{errors.source}</Form.Control.Feedback>
                                </Form.Group>
                                <Form.Group controlId="exampleForm.ControlTextarea99">
                                    <Form.Label>Targeted App</Form.Label>
                                    <Form.Control
                                        as="select"
                                        name="targetAppId"
                                        value={values.targetAppId}
                                        onChange={handleChange}
                                        isInvalid={!!errors.targetAppId}
                                    >
                                        <option value="">Choose App</option>
                                        {apps.map((appdata) => (
                                            <option key={appdata.appId} value={appdata.appId}>
                                                {appdata.name}
                                            </option>
                                        ))}
                                    </Form.Control>
                                    <Form.Control.Feedback type="invalid">{errors.targetAppId}</Form.Control.Feedback>
                                </Form.Group>
                                <Form.Group controlId="sharedKeyCTRLID">
                                    <Form.Label>Expiration</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="expiration"
                                        value={values.expiration}
                                        hidden
                                        onChange={() => void 0}
                                    />
                                    <InputGroup>
                                        <Form.Control
                                            type="text"
                                            name="expiration_date_text"
                                            value={
                                                values.expiration
                                                    ? dayjs(values.expiration).format('DD/MM/YYYY')
                                                    : 'Never'
                                            }
                                            disabled
                                            style={{
                                                textAlign: 'center',
                                                color: '#c9002d',
                                                backgroundColor: '#e9e9b5',
                                                fontWeight: 'bold',
                                            }}
                                        />
                                        <Button
                                            variant="outline-dark"
                                            className="inputGrpButton dynamicJsHoverColorIcon"
                                            onClick={() => setShowDateSwitchs(!showDateSwitchs)}
                                        >
                                            {showDateSwitchs ? (
                                                <AiOutlineArrowUp
                                                    style={{
                                                        fontSize: '9pt',
                                                        color: '#000',
                                                        '--my-hover-icon-color': '#fff',
                                                        cursor: 'pointer',
                                                    }}
                                                />
                                            ) : (
                                                <AiOutlineArrowDown
                                                    style={{
                                                        fontSize: '9pt',
                                                        color: '#000',
                                                        '--my-hover-icon-color': '#fff',
                                                        cursor: 'pointer',
                                                    }}
                                                />
                                            )}
                                        </Button>
                                    </InputGroup>
                                    {showDateSwitchs && (
                                        <div style={{ textAlign: 'center' }}>
                                            <Form.Check
                                                inline
                                                label="Never"
                                                name="exp_grp"
                                                type="switch"
                                                id="inline-switch-1"
                                                checked={expire ? false : true}
                                                onChange={() => {
                                                    setExpire(null);
                                                    handleChange({
                                                        target: {
                                                            value: null,
                                                            name: 'expiration',
                                                        },
                                                    });
                                                }}
                                            />
                                            <Form.Check
                                                inline
                                                label="1 Week"
                                                name="exp_grp"
                                                type="switch"
                                                id="inline-switch-2"
                                                checked={expire === 'week' ? true : false}
                                                onChange={() => {
                                                    setExpire('week');
                                                    handleChange({
                                                        target: {
                                                            value: `${dayjs()
                                                                .add(1, 'week')
                                                                .format('YYYY-MM-DD')}T00:00:00`,
                                                            name: 'expiration',
                                                        },
                                                    });
                                                }}
                                            />
                                            <Form.Check
                                                inline
                                                label="3 Months"
                                                name="exp_grp"
                                                type="switch"
                                                id="inline-switch-3"
                                                checked={expire === 'quarter' ? true : false}
                                                onChange={() => {
                                                    setExpire('quarter');
                                                    handleChange({
                                                        target: {
                                                            value: `${dayjs()
                                                                .add(3, 'month')
                                                                .format('YYYY-MM-DD')}T00:00:00`,
                                                            name: 'expiration',
                                                        },
                                                    });
                                                }}
                                            />
                                            <Form.Check
                                                inline
                                                label="1 Year"
                                                name="exp_grp"
                                                type="switch"
                                                id="inline-switch-4"
                                                checked={expire === 'year' ? true : false}
                                                onChange={() => {
                                                    setExpire('year');
                                                    handleChange({
                                                        target: {
                                                            value: `${dayjs()
                                                                .add(1, 'year')
                                                                .format('YYYY-MM-DD')}T00:00:00`,
                                                            name: 'expiration',
                                                        },
                                                    });
                                                }}
                                            />
                                        </div>
                                    )}
                                </Form.Group>

                                <Form.Group controlId="sharedKeyCTRLID">
                                    <Form.Label>Shared Key</Form.Label>
                                    <InputGroup>
                                        <Form.Control
                                            type="text"
                                            placeholder="Shared Key"
                                            name="sharedKey"
                                            value={values.sharedKey}
                                            onChange={handleChange}
                                            isInvalid={!!errors.sharedKey}
                                            readOnly
                                            style={{
                                                textAlign: 'center',
                                                color: 'rgb(18 169 179)',
                                                backgroundColor: 'rgb(241 253 227)',
                                                fontWeight: 'bold',
                                            }}
                                        />
                                        {!formModalEditMode && (
                                            <Button
                                                variant="outline-info"
                                                className="inputGrpButton"
                                                onClick={async () => {
                                                    const genKey = await generateSharedKey();
                                                    handleChange({
                                                        target: {
                                                            value: genKey,
                                                            name: 'sharedKey',
                                                        },
                                                    });
                                                }}
                                            >
                                                Generate
                                            </Button>
                                        )}
                                    </InputGroup>
                                    <Form.Control.Feedback type="invalid">{errors.sharedKey}</Form.Control.Feedback>
                                </Form.Group>

                                <Form.Group controlId="formBasicCheckbox3">
                                    <Form.Check
                                        type="checkbox"
                                        label="Pass"
                                        name="allRoutesPass"
                                        checked={values.allRoutesPass}
                                        onChange={({ target: { checked: checkValue } }) => {
                                            handleChange({
                                                target: {
                                                    value: checkValue,
                                                    name: 'allRoutesPass',
                                                },
                                            });
                                            if (!!checkValue)
                                                handleChange({
                                                    target: {
                                                        value: null,
                                                        name: 'routeNamesAllowed',
                                                    },
                                                });
                                        }}
                                        isInvalid={!!errors.allRoutesPass}
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.allRoutesPass}</Form.Control.Feedback>
                                </Form.Group>
                                {/* routeNames */}
                                <Form.Group controlId="routeNamesCtrlGrp">
                                    <Form.Label>Route names allowed</Form.Label>
                                    <Button
                                        variant="outline-success dynamicJsHoverColorIcon float-right mb-1"
                                        onClick={() =>
                                            handleChange({
                                                target: {
                                                    value: [
                                                        ...(values.routeNamesAllowed ? values.routeNamesAllowed : []),
                                                        '',
                                                    ],
                                                    name: 'routeNamesAllowed',
                                                },
                                            })
                                        }
                                    >
                                        <FiPlus
                                            style={{
                                                fontSize: '9pt',
                                                color: 'green',
                                                '--my-hover-icon-color': '#fff',
                                                cursor: 'pointer',
                                            }}
                                        />
                                    </Button>
                                    <FieldArray
                                        name="routeNamesAllowed"
                                        render={() =>
                                            values.routeNamesAllowed &&
                                            values.routeNamesAllowed.map((rnData, index) => {
                                                const rnErrors =
                                                    errors.routeNamesAllowed?.length && errors.routeNamesAllowed[index];
                                                return (
                                                    <InputGroup key={index} className="mt-1">
                                                        <Field
                                                            name={`routeNamesAllowed[${index}]`}
                                                            type="text"
                                                            placeholder="Route Name"
                                                            className={`form-control ${
                                                                !!rnErrors ? ' is-invalid' : ''
                                                            }`}
                                                        />
                                                        <Button
                                                            variant="outline-warning"
                                                            className="inputGrpButton dynamicJsHoverColorIcon"
                                                            onClick={() => {
                                                                const updatedArr = [...values.routeNamesAllowed].filter(
                                                                    (dvr, i) => i !== index,
                                                                );

                                                                handleChange({
                                                                    target: {
                                                                        value: updatedArr,
                                                                        name: 'routeNamesAllowed',
                                                                    },
                                                                });
                                                            }}
                                                        >
                                                            <FiMinus
                                                                style={{
                                                                    fontSize: '9pt',
                                                                    color: '#ff0000',
                                                                    '--my-hover-icon-color': '#fff',
                                                                    cursor: 'pointer',
                                                                }}
                                                            />
                                                        </Button>
                                                    </InputGroup>
                                                );
                                            })
                                        }
                                    />
                                </Form.Group>
                                <Button variant="primary" type="submit" block>
                                    Submit
                                </Button>
                            </Form>
                        )}
                    </Formik>
                </div>
            </CustomModal>
        </>
    );
};

export default Apps;
